import { SupportActionType, SupportRequestLane } from '@/constants/supportRequests';
import DateUtils from './date.utils';

/**
 * Lógica de la bandeja de aprobación (KB-907) que no depende de Vue, así se
 * puede testear sin montar componentes (este repo no tiene @vue/test-utils
 * ni un entorno DOM configurado en vitest — ver vitest.config.ts).
 */

export interface ChangeLike {
  field: string;
  from: string | null;
  to: string | null;
}

export interface ChangeRow {
  field: string;
  label: string;
  from: string;
  to: string;
}

/**
 * Convierte `action.changes` (backend-v2) en filas listas para renderizar:
 * una por cambio, con la etiqueta del campo ya traducida y `null` (vacío)
 * resuelto a `emptyLabel`. Devuelve `[]` cuando `changes` no viene o viene
 * vacío, para que el llamador pueda caer al volcado del payload crudo.
 */
export function mapChangesToRows(
  changes: ChangeLike[] | null | undefined,
  translateLabel: (field: string) => string,
  emptyLabel: string,
): ChangeRow[] {
  if (!changes || !changes.length) return [];
  return changes.map((change) => ({
    field: change.field,
    label: translateLabel(change.field),
    from: change.from === null ? emptyLabel : change.from,
    to: change.to === null ? emptyLabel : change.to,
  }));
}

export interface ActionTypeCountEntry {
  type: string;
  count: number;
}

export interface ActionCountsLike {
  byActionType?: Record<string, number> | null;
  byActionTypeAndLane?: Partial<Record<SupportRequestLane, Record<string, number>>> | null;
}

/**
 * Cuenta por tipo de gestión, acotada al carril activo. Usa
 * `byActionTypeAndLane[lane]` cuando el backend lo manda; si no viene (PR de
 * backend todavía no mergeado, o backend viejo) cae al `byActionType` global
 * de siempre. En ambos casos oculta los tipos en cero, para que un chip nunca
 * quede mostrando una cantidad que la lista filtrada no puede tener.
 */
export function actionTypeCountsForLane(
  counts: ActionCountsLike,
  lane: SupportRequestLane,
): ActionTypeCountEntry[] {
  const laneCounts = counts.byActionTypeAndLane?.[lane];
  const source = laneCounts || counts.byActionType || {};
  return Object.entries(source)
    .map(([type, count]) => ({ type, count: count || 0 }))
    .filter((entry) => entry.count > 0)
    .sort((a, b) => b.count - a.count);
}

export interface ApprovableActionLike {
  lane?: SupportRequestLane | null;
  manualReason?: string | null;
}

/**
 * Una gestión con `manualReason` (o clasificada en el carril Manual) necesita
 * revisión humana antes de poder aplicarse: no debe poder aprobarse con un
 * click desde la bandeja. "Rechazar" sigue disponible siempre.
 *
 * El chequeo de `manualReason` es redundante con el de `lane`: en backend-v2,
 * `computeLane` (lane.util.ts) manda a MANUAL toda acción con `manualReason`
 * seteado, así que nunca debería llegar acá con otro carril. Se deja la
 * redundancia a propósito — no la "simplifiques" a solo `lane` — porque
 * cubre el caso en que `lane` viene `null` (pasa cuando `status` no es
 * PROPOSED, ver isReviewMode en la card) y porque el gate del cliente no
 * debe depender de que el backend derive el carril correctamente.
 */
export function canApproveInLane(action: ApprovableActionLike): boolean {
  return action.lane !== SupportRequestLane.MANUAL && !action.manualReason;
}

/** Tipos de gestión cuyo target es un docente en vez de un alumno (KB-907). */
const TEACHER_ACTION_TYPES: string[] = [
  SupportActionType.TEACHER_CREATE,
  SupportActionType.TEACHER_REASSIGN_COURSE,
];

/**
 * Ruta a la ficha de edición del alumno/docente resuelto, para que el nombre
 * sea clickeable en la bandeja (KB-907). Sigue el mismo patrón que ya usa el
 * resto del admin (`/edit/students/:id`, `/edit/teachers/:id` — ver
 * UsersDataTable.vue). Sin `bookuserId` (target NOT_FOUND, o candidato sin
 * resolver) no hay a dónde linkear.
 */
export function userProfileRoute(
  actionType: string,
  bookuserId: number | null | undefined,
): string | null {
  if (!bookuserId) return null;
  return TEACHER_ACTION_TYPES.includes(actionType)
    ? `/edit/teachers/${bookuserId}`
    : `/edit/students/${bookuserId}`;
}

/**
 * Formatea una fecha de nacimiento (sólo fecha, sin hora) para mostrarla en
 * la lista de candidatos ambiguos — es uno de los datos que sirve para
 * distinguir hermanos (KB-907). Pasar un string tipo "1998-03-15" directo por
 * `new Date(...)` lo interpreta como medianoche UTC, y en Montevideo
 * (UTC-3) `toLocaleDateString()` lo muestra corrido un día. Se normaliza con
 * `DateUtils.normalizeDateToYYYYMMDD` y se arma la fecha con componentes
 * locales para evitar el corrimiento.
 */
export function formatBirthDate(
  birthDate: string | null | undefined,
  emptyLabel: string,
): string {
  const normalized = DateUtils.normalizeDateToYYYYMMDD(birthDate);
  if (!normalized) return emptyLabel;
  const [year, month, day] = normalized.split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString();
}
